// Cloud-storage plan tiers, derived from Seal subscription titles.
// Source of truth for storage quotas + per-file size caps used by the
// "My Projects" upload pipeline. Read-only configuration.

export type PlanTier =
  | "free"
  | "paintbooth"
  | "vehicle_dev"
  | "server_dev"
  | "all_services"
  | "studio";

export const PLAN_TIER_RANK: Record<PlanTier, number> = {
  free: 0,
  paintbooth: 1,
  vehicle_dev: 2,
  server_dev: 3,
  all_services: 4,
  studio: 5,
};

const GB = 1024 ** 3;
const MB = 1024 ** 2;

export const PLAN_QUOTAS: Record<
  PlanTier,
  {
    label: string;
    storageBytes: number;
    maxFileBytes: number;
    allowsCloudSave: boolean;
    aiTokensPerMonth: number;
  }
> = {
  free: {
    label: "Free Editor",
    storageBytes: 0,
    maxFileBytes: 0,
    allowsCloudSave: false,
    aiTokensPerMonth: 0,
  },
  paintbooth: {
    label: "Paint Booth Only",
    storageBytes: 2 * GB,
    maxFileBytes: 250 * MB,
    allowsCloudSave: true,
    aiTokensPerMonth: 0,
  },
  vehicle_dev: {
    label: "Vehicle Developer Apps",
    storageBytes: 10 * GB,
    maxFileBytes: 500 * MB,
    allowsCloudSave: true,
    aiTokensPerMonth: 0,
  },
  server_dev: {
    label: "Server Developer Apps + AI",
    storageBytes: 25 * GB,
    maxFileBytes: 1 * GB,
    allowsCloudSave: true,
    aiTokensPerMonth: 300,
  },
  all_services: {
    label: "All Services",
    storageBytes: 50 * GB,
    maxFileBytes: 1 * GB,
    allowsCloudSave: true,
    aiTokensPerMonth: 750,
  },
  studio: {
    label: "Studio",
    storageBytes: 150 * GB,
    maxFileBytes: 2 * GB,
    allowsCloudSave: true,
    aiTokensPerMonth: 2000,
  },
};

export function planFor(tier: PlanTier) {
  return PLAN_QUOTAS[tier];
}

/**
 * Whether the user can use AI tools / has a token allowance to spend.
 * Admins always pass; otherwise gated on a plan that grants monthly AI tokens.
 */
export function planHasAiAccess(tier: PlanTier, isAdmin = false): boolean {
  if (isAdmin) return true;
  return PLAN_QUOTAS[tier].aiTokensPerMonth > 0;
}

export function comparePlanTiers(a: PlanTier, b: PlanTier) {
  return PLAN_TIER_RANK[a] - PLAN_TIER_RANK[b];
}

// Feature-inclusion lattice: for each tier, the set of tiers whose FULL toolset
// it contains (itself included). This is deliberately NOT PLAN_TIER_RANK —
// Vehicle Developer and Server Developer are PARALLEL tracks, so neither
// subsumes the other even though server_dev outranks vehicle_dev on price.
// Only Pro (all_services) and Studio sit above both tracks. A linear rank
// comparison would wrongly report that Server Developer "includes" Vehicle
// Developer. Keep in sync with the pricing matrix + FEATURES_BY_VARIANT_ID.
const PLAN_INCLUDED_TIERS: Record<PlanTier, ReadonlySet<PlanTier>> = {
  free: new Set<PlanTier>(["free"]),
  paintbooth: new Set<PlanTier>(["free", "paintbooth"]),
  // Vehicle track carries the Paint Booth tool, so it subsumes paintbooth.
  vehicle_dev: new Set<PlanTier>(["free", "paintbooth", "vehicle_dev"]),
  // Server track shares no tools with the vehicle/paintbooth track.
  server_dev: new Set<PlanTier>(["free", "server_dev"]),
  all_services: new Set<PlanTier>([
    "free",
    "paintbooth",
    "vehicle_dev",
    "server_dev",
    "all_services",
  ]),
  studio: new Set<PlanTier>([
    "free",
    "paintbooth",
    "vehicle_dev",
    "server_dev",
    "all_services",
    "studio",
  ]),
};

/**
 * Does `plan` grant everything `candidate` grants — i.e. is `candidate`'s full
 * toolset a subset of `plan`'s? Unlike a PLAN_TIER_RANK comparison this respects
 * the parallel Vehicle/Server tracks: `planIncludes("server_dev", "vehicle_dev")`
 * is `false` (and vice versa), because neither track contains the other's tools.
 */
export function planIncludes(plan: PlanTier, candidate: PlanTier): boolean {
  return PLAN_INCLUDED_TIERS[plan].has(candidate);
}

// Subscription product titles (as recorded by Seal/Shopify) → PlanTier.
// Mirrors `PLAN_TIER_BY_TITLE` in src/lib/server/sealSubscriptions.ts; both
// sides import this same constant so they can't drift apart.
export const PLAN_TIER_BY_TITLE: Record<string, PlanTier> = {
  "The Workshop - Paint Booth": "paintbooth",
  "The Workshop - UV Studio": "vehicle_dev",
  "The Workshop - Easy Meta": "vehicle_dev",
  "The Workshop - Vehicle Developer Apps": "vehicle_dev",
  "The Workshop - All Vehicle Developer Apps": "vehicle_dev",
  "The Workshop - Server Developer Apps": "server_dev",
  "The Workshop - All Server Developer Apps": "server_dev",
  "The Workshop - All Services": "all_services",
  "The Workshop - All Features": "all_services",
  "The Workshop - Studio": "studio",
  "The Workshop V2 - Paint Booth": "paintbooth",
  "The Workshop V2 - All Vehicle Developer Apps": "vehicle_dev",
  "The Workshop V2 - All Server Developer Apps": "server_dev",
  "The Workshop V2 - Pro": "all_services",
  "The Workshop V2 - Studio": "studio",
};

// Canonical Seal item title to WRITE for each paid tier (in-app plan changes
// rename the line item on upgrade). These must be keys of PLAN_TIER_BY_TITLE
// *and* of FEATURES_BY_TITLE in sealSubscriptions.ts: the title is the
// entitlement fallback whenever a Seal payload omits variant_id, and it feeds
// the seal-webhook/Discord/order pipelines — a title outside the maps silently
// breaks all of those for the subscription. Do NOT build titles from
// PLAN_QUOTAS labels; the labels are UI copy and don't match the catalog.
export const CANONICAL_SEAL_TITLE_BY_TIER: Partial<Record<PlanTier, string>> = {
  paintbooth: "The Workshop V2 - Paint Booth",
  vehicle_dev: "The Workshop V2 - All Vehicle Developer Apps",
  server_dev: "The Workshop V2 - All Server Developer Apps",
  all_services: "The Workshop V2 - Pro",
  studio: "The Workshop V2 - Studio",
};

// New subscription product is a single Shopify product with one variant per
// tier. Match by Shopify variant_id (preferred when Seal exposes it) — falls
// back to PLAN_TIER_BY_TITLE for legacy subscribers whose items only carry
// the old product title.
export const PLAN_TIER_BY_VARIANT_ID: Record<string, PlanTier> = {
  "51995955102016": "vehicle_dev",   // Vehicle Developer Apps
  "51995955167552": "server_dev",    // Server Developer Apps + AI
  "51995955200320": "all_services",  // All Services (Shopify internal name: "Pro")
  "51995969585472": "studio",        // Studio
};

/**
 * Resolve the user's current PlanTier from their `personalSubscriptionData`
 * (the shape exposed on the auth context's `billing` object). Picks the
 * highest-ranking active subscription title across all matched subscriptions.
 */
export function deriveCurrentPlan(
  data: Record<string, unknown> | null | undefined,
): PlanTier {
  if (!data || typeof data !== "object" || Array.isArray(data)) return "free";
  const matched = (
    data as {
      matchedSubscriptions?: Array<{
        itemTitles?: unknown;
        itemVariantIds?: unknown;
        accessActive?: unknown;
      }>;
    }
  ).matchedSubscriptions;
  if (!Array.isArray(matched) || matched.length === 0) return "free";
  let best: PlanTier = "free";
  for (const subscription of matched) {
    if (!subscription || subscription.accessActive === false) continue;

    // Prefer variant_id match (new subscriptions), fall back to title (legacy).
    const variantIds = subscription.itemVariantIds;
    if (Array.isArray(variantIds)) {
      for (const variantId of variantIds) {
        if (typeof variantId !== "string") continue;
        const tier = PLAN_TIER_BY_VARIANT_ID[variantId];
        if (tier && PLAN_TIER_RANK[tier] > PLAN_TIER_RANK[best]) {
          best = tier;
        }
      }
    }

    const titles = subscription.itemTitles;
    if (Array.isArray(titles)) {
      for (const title of titles) {
        if (typeof title !== "string") continue;
        const tier = PLAN_TIER_BY_TITLE[title];
        if (tier && PLAN_TIER_RANK[tier] > PLAN_TIER_RANK[best]) {
          best = tier;
        }
      }
    }
  }
  return best;
}
